import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, Button, IconButton, Paper,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Divider, Dialog, DialogActions, TextField,
  Alert, Tooltip,
} from "@mui/material";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon     from "@mui/icons-material/CancelOutlined";
import PersonOutlineIcon      from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon      from "@mui/icons-material/CalendarToday";
import BusinessIcon           from "@mui/icons-material/Business";
import ReceiptLongIcon        from "@mui/icons-material/ReceiptLong";
import Inventory2Icon         from "@mui/icons-material/Inventory2";
import NotesIcon              from "@mui/icons-material/Notes";
import WarningAmberIcon       from "@mui/icons-material/WarningAmber";
import CheckCircleIcon        from "@mui/icons-material/CheckCircle";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PriorityHighIcon       from "@mui/icons-material/PriorityHigh";
import LinkIcon               from "@mui/icons-material/Link";
import api from "../../../services/api";

const C = {
  primary:   "#2563EB", primaryBg: "#EFF6FF",
  surface:   "#FFFFFF", surfaceAlt: "#F8FAFC",
  border:    "#E2E8F0", borderHov:  "#CBD5E1",
  text1:     "#0F172A", text2:      "#475569", text3: "#94A3B8",
  success:   "#16A34A", successBg:  "#F0FDF4", successBdr: "#BBF7D0",
  warn:      "#D97706", warnBg:     "#FFFBEB", warnBdr:    "#FDE68A",
  danger:    "#DC2626", dangerBg:   "#FEF2F2", dangerBdr:  "#FECACA",
  infoBdr:   "#BFDBFE",
  neutral:   "#64748B", neutralBg:  "#F1F5F9", neutralBdr: "#CBD5E1",
};

const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface, fontSize: "0.82rem",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
};

const thSx = {
  fontSize: "0.72rem", fontWeight: 700, color: C.text3,
  borderBottom: `1px solid ${C.border}`, py: "10px", px: "14px",
  textTransform: "uppercase", letterSpacing: "0.04em",
  whiteSpace: "nowrap", backgroundColor: "transparent",
};
const tdSx = { py: "10px", px: "14px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" };

const STATUS_CFG = {
  pending:   { label: "Pending",   color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  disetujui: { label: "Disetujui", color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  ditolak:   { label: "Ditolak",   color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};
function getStatusCfg(status) {
  if (!status) return { label: "—", color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
  return STATUS_CFG[status.toLowerCase()] || { label: status, color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
}
function StatusChip({ value }) {
  const cfg = getStatusCfg(value);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px" }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

const SIFAT_CFG = {
  biasa:    { label: "Biasa",    color: C.neutral, bg: C.neutralBg, border: C.neutralBdr },
  segera:   { label: "Segera",   color: C.warn,    bg: C.warnBg,    border: C.warnBdr    },
  mendesak: { label: "Mendesak", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr  },
};
function SifatChip({ value }) {
  if (!value) return <Typography sx={{ fontSize: "0.85rem", color: C.text1, fontWeight: 500 }}>—</Typography>;
  const cfg = SIFAT_CFG[value.toLowerCase()] || { label: value, color: C.neutral, bg: C.neutralBg, border: C.neutralBdr };
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px" }}>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

function InfoRow({ icon, label, value, customValue }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: "11px",
      borderBottom: `1px solid ${C.border}`, "&:last-child": { borderBottom: "none", pb: 0 } }}>
      <Box sx={{ color: C.text3, mt: 0.1, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{ fontSize: "0.68rem", color: C.text3, mb: 0.3,
          textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
          {label}
        </Typography>
        {customValue ?? (
          <Typography sx={{ fontSize: "0.85rem", color: C.text1, fontWeight: 500 }}>
            {value || "—"}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function formatRupiah(value) {
  if (!value && value !== 0) return "Rp\u00A00";
  return "Rp\u00A0" + parseFloat(value).toLocaleString("id-ID");
}
function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
}

export default function DetailProcurement() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const [updating,     setUpdating]     = useState(null);
  const [approveOpen,  setApproveOpen]  = useState(false);
  const [rejectOpen,   setRejectOpen]   = useState(false);
  const [rejectNote,   setRejectNote]   = useState("");

  const [itemAction,     setItemAction]     = useState(null);
  const [itemRejectNote, setItemRejectNote] = useState("");
  const [itemUpdating,   setItemUpdating]   = useState(null);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/procurements/${id}`);
      setData(res.data?.data ?? res.data);
    } catch { setError("Gagal memuat detail pengajuan."); }
    finally  { setLoading(false); }
  };

  useEffect(() => { fetchDetail(); }, [id]);

  const handleApprove = async () => {
    try {
      setUpdating("disetujui");
      await api.put(`/procurements/${id}/status`, { status: "disetujui" });
      setApproveOpen(false);
      await fetchDetail();
    } catch { setError("Gagal menyetujui pengajuan."); }
    finally  { setUpdating(null); }
  };

  const handleReject = async () => {
    try {
      setUpdating("ditolak");
      await api.put(`/procurements/${id}/status`, { status: "ditolak", note: rejectNote });
      setRejectOpen(false); setRejectNote("");
      await fetchDetail();
    } catch { setError("Gagal menolak pengajuan."); }
    finally  { setUpdating(null); }
  };

  const handleApproveItem = async () => {
    if (!itemAction) return;
    try {
      setItemUpdating(itemAction.item.id);
      await api.post(`/procurements/${id}/items/${itemAction.item.id}/approve`);
      setItemAction(null);
      await fetchDetail();
    } catch { setError("Gagal menyetujui item."); }
    finally  { setItemUpdating(null); }
  };

  const handleRejectItem = async () => {
    if (!itemAction) return;
    try {
      setItemUpdating(itemAction.item.id);
      await api.post(`/procurements/${id}/items/${itemAction.item.id}/reject`, { note: itemRejectNote });
      setItemAction(null); setItemRejectNote("");
      await fetchDetail();
    } catch { setError("Gagal menolak item."); }
    finally  { setItemUpdating(null); }
  };

  if (loading) return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center",
      alignItems: "center", minHeight: 400, gap: 2 }}>
      <CircularProgress size={32} thickness={4} sx={{ color: C.primary }} />
      <Typography sx={{ fontSize: "0.82rem", color: C.text3 }}>Memuat data pengajuan...</Typography>
    </Box>
  );

  if (error || !data) return (
    <Box sx={{ py: 8, textAlign: "center" }}>
      <Alert severity="error" sx={{ borderRadius: "8px", border: `1px solid ${C.dangerBdr}`, mb: 2 }}>
        {error || "Data tidak ditemukan."}
      </Alert>
      <Button onClick={() => navigate(-1)} variant="outlined"
        sx={{ borderRadius: "8px", textTransform: "none" }}>Kembali</Button>
    </Box>
  );

  const sc      = getStatusCfg(data.status?.name);
  const items   = data.items || [];
  const total   = items.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
  const hasLink = items.some(i => i.link_produk);
  const hasAnyPendingItem = items.some(i => (i.item_status || "pending") === "pending");

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{
            border: `1px solid ${C.border}`, borderRadius: "8px", width: 32, height: 32,
            color: C.text2, backgroundColor: C.surface,
            "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg },
          }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: sc.bg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ReceiptLongIcon sx={{ fontSize: 20, color: sc.color }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: C.text1, lineHeight: 1.2 }}>
                  Detail Pengajuan
                </Typography>
                <StatusChip value={data.status?.name} />
              </Box>
              <Typography sx={{ fontSize: "0.75rem", color: C.text3, mt: 0.25,
                fontFamily: "'JetBrains Mono', monospace" }}>
                {data.request_number}
              </Typography>
            </Box>
          </Box>
        </Box>

        {hasAnyPendingItem && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" size="small"
              startIcon={<CancelOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => setRejectOpen(true)} disabled={!!updating}
              sx={{
                height: 34, borderRadius: "8px", textTransform: "none", fontSize: "0.82rem",
                borderColor: C.dangerBdr, color: C.danger,
                "&:hover": { backgroundColor: C.dangerBg, borderColor: C.danger },
              }}>
              Tolak Semua
            </Button>
            <Button variant="contained" size="small"
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => setApproveOpen(true)} disabled={!!updating}
              sx={{
                height: 34, borderRadius: "8px", textTransform: "none", fontSize: "0.82rem",
                fontWeight: 600, backgroundColor: C.success, boxShadow: "none",
                "&:hover": { backgroundColor: "#15803D", boxShadow: "none" },
              }}>
              Setujui Semua
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}
          sx={{ mb: 2, borderRadius: "8px", border: `1px solid ${C.dangerBdr}` }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2.5, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>

        {/* ── Sidebar ── */}
        <Box sx={{ flex: "0 0 300px", display: "flex", flexDirection: "column", gap: 2 }}>

          <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
            <Box sx={{ px: 2, py: "11px", borderBottom: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.text3,
                textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Informasi Pengajuan
              </Typography>
            </Box>
            <Box sx={{ px: 2, py: 1 }}>
              <InfoRow icon={<ReceiptLongIcon sx={{ fontSize: 15 }} />}      label="No. Pengajuan"    value={data.request_number} />
              <InfoRow icon={<CalendarTodayIcon sx={{ fontSize: 15 }} />}    label="Tanggal Diajukan" value={formatDate(data.request_date)} />
              <InfoRow icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />}    label="Nama Pemohon"     value={data.requester_name || data.user?.name} />
              <InfoRow icon={<BusinessIcon sx={{ fontSize: 15 }} />}         label="Departemen / Unit" value={data.department || data.unit?.name} />
              <InfoRow icon={<AssignmentOutlinedIcon sx={{ fontSize: 15 }} />} label="Digunakan Untuk" value={data.used_for} />
              <InfoRow icon={<PriorityHighIcon sx={{ fontSize: 15 }} />}     label="Sifat Permintaan" customValue={<SifatChip value={data.request_type} />} />
              <InfoRow icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />}    label="Akun Pengaju"     value={data.user?.name} />
            </Box>
          </Paper>

          {data.description && (
            <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <NotesIcon sx={{ fontSize: 15, color: C.text3 }} />
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.text3,
                  textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Catatan Tambahan
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.82rem", color: C.text2, lineHeight: 1.6 }}>
                {data.description}
              </Typography>
            </Paper>
          )}

          <Paper elevation={0} sx={{
            border: `1px solid ${C.infoBdr}`, borderRadius: "12px", p: 2,
            background: `linear-gradient(135deg, ${C.primaryBg} 0%, #E0EEFF 100%)`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Inventory2Icon sx={{ fontSize: 15, color: C.primary }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.primary,
                textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Ringkasan
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>Total Item</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: C.text1 }}>{items.length}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.success }}>Disetujui</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: C.success }}>
                {items.filter(i => i.item_status === "disetujui").length}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.danger }}>Ditolak</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: C.danger }}>
                {items.filter(i => i.item_status === "ditolak").length}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.warn }}>Pending</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: C.warn }}>
                {items.filter(i => !i.item_status || i.item_status === "pending").length}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: C.infoBdr, mb: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2, fontWeight: 600 }}>Total</Typography>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: C.primary, letterSpacing: "-0.02em" }}>
                {formatRupiah(total)}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* ── Tabel Items ── */}
        <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px",
          overflow: "hidden", flexGrow: 1 }}>
          <Box sx={{ px: 2, py: "11px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", gap: 1, backgroundColor: C.surfaceAlt }}>
            <Inventory2Icon sx={{ fontSize: 16, color: C.primary }} />
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: C.text1 }}>
              Daftar Barang
            </Typography>
            <Box sx={{ display: "inline-flex", alignItems: "center",
              px: "7px", height: 20, backgroundColor: C.primaryBg,
              border: `1px solid ${C.infoBdr}`, borderRadius: "5px" }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: C.primary }}>
                {items.length} item
              </Typography>
            </Box>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: hasLink ? 780 : 640 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                  {[
                    "No", "Nama Barang", "Qty", "Harga Satuan", "Subtotal", "Status", "Link Produk", "Aksi",
                  ].map(h => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                      <Typography sx={{ fontSize: "0.82rem", color: C.text3 }}>Tidak ada item</Typography>
                    </TableCell>
                  </TableRow>
                ) : items.map((item, idx) => {
                  const itemStatus = item.item_status || "pending";
                  const isItemPending = itemStatus === "pending";
                  const isProcessingThis = itemUpdating === item.id;

                  return (
                    <TableRow key={item.id || idx} sx={{
                      "&:hover": { backgroundColor: C.surfaceAlt },
                      transition: "background 0.12s",
                    }}>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600 }}>
                          {idx + 1}
                        </Typography>
                      </TableCell>

                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: "0.85rem", color: C.text1, fontWeight: 600 }}>
                          {item.item_name}
                        </Typography>
                        {item.notes && (
                          <Typography sx={{ fontSize: "0.72rem", color: C.text3, mt: 0.25 }}>
                            {item.notes}
                          </Typography>
                        )}
                        {item.approvedBy && item.approved_at && (
                          <Typography sx={{ fontSize: "0.68rem", color: C.text3, mt: 0.25 }}>
                            oleh {item.approvedBy?.name ?? "Admin"} · {formatDate(item.approved_at)}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                          minWidth: 28, height: 22, px: 1,
                          borderRadius: "6px", backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }}>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: C.text1 }}>
                            {item.quantity}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: "0.82rem", color: C.text2 }}>
                          {formatRupiah(item.price)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: C.text1 }}>
                          {formatRupiah(item.subtotal)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={tdSx}>
                        <StatusChip value={itemStatus} />
                      </TableCell>

                      {/* ── Link Produk (selalu tampil) ── */}
                      <TableCell sx={tdSx}>
                        {item.link_produk ? (
                          <Tooltip title={item.link_produk} arrow placement="top">
                            <Box
                              component="a"
                              href={item.link_produk}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: "inline-flex", alignItems: "center", gap: 0.5,
                                px: "8px", py: "3px", borderRadius: "6px",
                                backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
                                fontSize: "0.72rem", color: C.primary, fontWeight: 600,
                                textDecoration: "none",
                                "&:hover": { backgroundColor: "#DBEAFE", borderColor: C.primary },
                                transition: "all 0.15s",
                              }}
                            >
                              <LinkIcon sx={{ fontSize: 11 }} />
                              Lihat
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: "0.78rem", color: C.text3 }}>—</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={tdSx}>
                        {isItemPending ? (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Setujui item ini" arrow>
                              <span>
                                <IconButton size="small"
                                  disabled={isProcessingThis}
                                  onClick={() => setItemAction({ type: "approve", item })}
                                  sx={{
                                    width: 28, height: 28, borderRadius: "6px",
                                    border: `1px solid ${C.successBdr}`,
                                    color: C.success, backgroundColor: C.successBg,
                                    "&:hover": { backgroundColor: "#DCFCE7" },
                                    "&:disabled": { opacity: 0.5 },
                                  }}>
                                  {isProcessingThis
                                    ? <CircularProgress size={12} sx={{ color: C.success }} />
                                    : <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Tolak item ini" arrow>
                              <span>
                                <IconButton size="small"
                                  disabled={isProcessingThis}
                                  onClick={() => setItemAction({ type: "reject", item })}
                                  sx={{
                                    width: 28, height: 28, borderRadius: "6px",
                                    border: `1px solid ${C.dangerBdr}`,
                                    color: C.danger, backgroundColor: C.dangerBg,
                                    "&:hover": { backgroundColor: "#FEE2E2" },
                                    "&:disabled": { opacity: 0.5 },
                                  }}>
                                  <CancelOutlinedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ px: 2, py: "12px", borderTop: `1px solid ${C.border}`,
            display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 3,
            backgroundColor: C.surfaceAlt }}>
            <Typography sx={{ fontSize: "0.8rem", color: C.text3, fontWeight: 600,
              textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Total Pengajuan
            </Typography>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: C.text1, letterSpacing: "-0.02em" }}>
              {formatRupiah(total)}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* ── Dialog Setujui Semua ── */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.successBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <CheckCircleIcon sx={{ fontSize: 20, color: C.success }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Setujui Semua Item?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Semua item pending pada pengajuan <strong>{data.request_number}</strong> akan disetujui sekaligus.
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setApproveOpen(false)} disabled={!!updating}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleApprove} disabled={!!updating} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2.5, fontWeight: 600,
              backgroundColor: C.success, boxShadow: "none",
              "&:hover": { backgroundColor: "#15803D", boxShadow: "none" } }}>
            {updating === "disetujui" ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Ya, Setujui Semua"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Tolak Semua ── */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 16px" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Tolak Semua Item?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Semua item pending pada <strong>{data.request_number}</strong> akan ditolak.
              </Typography>
            </Box>
          </Box>
          <TextField fullWidth multiline rows={3} size="small"
            placeholder="Tulis alasan penolakan (opsional)..."
            value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
            sx={inputBase} />
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => { setRejectOpen(false); setRejectNote(""); }} disabled={!!updating}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleReject} disabled={!!updating} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2.5, fontWeight: 600,
              backgroundColor: C.danger, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            {updating === "ditolak" ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Ya, Tolak Semua"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Approve Item ── */}
      <Dialog open={itemAction?.type === "approve"} onClose={() => setItemAction(null)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.successBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <CheckCircleIcon sx={{ fontSize: 20, color: C.success }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Setujui Item Ini?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Item <strong>{itemAction?.item?.item_name}</strong> akan disetujui dan ditambahkan ke inventaris.
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setItemAction(null)} disabled={!!itemUpdating}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleApproveItem} disabled={!!itemUpdating} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2.5, fontWeight: 600,
              backgroundColor: C.success, boxShadow: "none",
              "&:hover": { backgroundColor: "#15803D", boxShadow: "none" } }}>
            {itemUpdating ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Ya, Setujui"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Reject Item ── */}
      <Dialog open={itemAction?.type === "reject"} onClose={() => setItemAction(null)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 16px" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Tolak Item Ini?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Item <strong>{itemAction?.item?.item_name}</strong> akan ditolak.
              </Typography>
            </Box>
          </Box>
          <TextField fullWidth multiline rows={3} size="small"
            placeholder="Tulis alasan penolakan item (opsional)..."
            value={itemRejectNote} onChange={(e) => setItemRejectNote(e.target.value)}
            sx={inputBase} />
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => { setItemAction(null); setItemRejectNote(""); }} disabled={!!itemUpdating}
            sx={{ textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt } }}>
            Batal
          </Button>
          <Button onClick={handleRejectItem} disabled={!!itemUpdating} variant="contained"
            sx={{ textTransform: "none", borderRadius: "8px", px: 2.5, fontWeight: 600,
              backgroundColor: C.danger, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" } }}>
            {itemUpdating ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Ya, Tolak"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
